import { type ReactNode } from 'react';
import { sanitiseString } from '@quickstock/shared';

/**
 * What a hand-typed name will actually be stored as.
 *
 * Every name the till writes goes through `sanitiseString` — accents folded,
 * punctuation regularised, whitespace collapsed, uppercased — and the cloud now
 * does the same, so a department typed as "soft drinks" is stored as "SOFT
 * DRINKS". Showing that while it is being typed rather than after saving is what
 * makes the rule visible: these columns are `UNIQUE`, so somebody typing a name
 * that normalises onto an existing one should see it before they submit, not as
 * a rejection afterwards.
 *
 * Rendered only when the stored form differs from what was typed, so it is
 * information rather than furniture — which means it appears as soon as anything
 * is typed in lower case, exactly when it is worth knowing.
 *
 * `before` puts something else on the same line, for a form that already has a
 * line of small print under the name field.
 */
export function StoredNameHint({
  name,
  before,
}: {
  name: string;
  before?: ReactNode;
}): React.JSX.Element | null {
  const stored = sanitiseString(name);
  const differs = name.trim() !== '' && stored !== name;

  if (before === undefined && !differs) return null;

  return (
    <p className="mb-4 text-2xs text-ink-faint">
      {before}
      {differs && (
        <>
          {before === undefined ? 'Stored as ' : ' · stored as '}
          <span className="qs-figure text-ink-muted">{stored}</span>
        </>
      )}
    </p>
  );
}
